import { asLink } from "@prismicio/client"
import { PrismicNextLink } from "@prismicio/next/pages"
import type { JSXMapSerializer } from "@prismicio/react"
import { noop } from "radashi"
import React from "react"

import { Analytics } from "@bounce/analytics"
import { PNImage, imgixBlueFilter } from "@bounce/web-components"

import { InteractionEvents } from "@/libs/analytics/events"
import type { Interpolations } from "@/libs/prismic/helpers"
import { interpolateText, linkResolver, interpolateLink } from "@/libs/prismic/helpers"
import { convertLegacyUrl } from "@/libs/router/convertLegacyUrl"
import { isExternalUrl } from "@/libs/router/isExternalUrl"

type HtmlSerializerOptions = {
  interpolations?: Interpolations
  disableImgBlueFilter?: boolean
}

/**
 * Overwrite the default htmlSerializer to add custom styles and interpolate text
 * @see https://github.com/prismicio/prismic-react/blob/master/src/react-server/PrismicRichText.tsx
 */
export const htmlSerializer = ({
  interpolations,
  disableImgBlueFilter = false,
}: HtmlSerializerOptions): JSXMapSerializer => {
  const interpolate = <T,>(children: T) => interpolateText(children, interpolations)

  return {
    heading1: ({ children, key }) => {
      const text = interpolate(children)
      return (
        <h1 className="title1 my-8 text-black" key={key}>
          {text}
        </h1>
      )
    },
    heading2: ({ children, key }) => {
      const text = interpolate(children)
      return (
        <h2 className="title2 my-8 text-black md:mb-9 md:mt-16" key={key}>
          {text}
        </h2>
      )
    },
    heading3: ({ children, key }) => {
      const text = interpolate(children)
      return (
        <h3 className="title3 my-4 text-black md:my-6" key={key}>
          {text}
        </h3>
      )
    },
    heading4: ({ children, key }) => {
      const text = interpolate(children)
      return (
        <h3 className="title4 my-2 text-black" key={key}>
          {text}
        </h3>
      )
    },
    heading5: ({ children, key }) => {
      const text = interpolate(children)
      return (
        <h3 className="title5 my-2 text-black" key={key}>
          {text}
        </h3>
      )
    },
    heading6: ({ children, key }) => {
      const text = interpolate(children)
      return (
        <h3 className="title6 my-2 text-black" key={key}>
          {text}
        </h3>
      )
    },
    paragraph: ({ children, key, node }) => {
      const text = interpolate(children)

      // Don't render empty paragraphs
      // <p></p> or <p> </p>
      if (!node.text.trim().length) {
        // returning false instead of null or undefined else it will render <p></p>
        return false
      }

      return (
        <p key={key} className="paragraph1 mb-6 text-black">
          {text}
        </p>
      )
    },
    preformatted: ({ children, key }) => {
      const text = interpolate(children)
      return <pre key={key}>{text}</pre>
    },
    strong: ({ children, key }) => {
      const text = interpolate(children)
      return <strong key={key}>{text}</strong>
    },
    em: ({ children, key }) => {
      const text = interpolate(children)
      return <em key={key}>{text}</em>
    },
    listItem: ({ children, key }) => {
      const text = interpolate(children)
      return (
        <li className="paragraph1 mb-6 text-black" key={key}>
          {text}
        </li>
      )
    },
    oListItem: ({ children, key }) => {
      const text = interpolate(children)
      return (
        <li className="paragraph1 mb-6 text-black" key={key}>
          {text}
        </li>
      )
    },
    list: ({ children, key }) => {
      const text = interpolate(children)
      return (
        <ul className="list-disc pl-5" key={key}>
          {text}
        </ul>
      )
    },
    oList: ({ children, key }) => {
      const text = interpolate(children)
      return (
        <ol className="list-decimal pl-5" key={key}>
          {text}
        </ol>
      )
    },
    label: ({ children, key }) => {
      const text = interpolate(children)
      return <span key={key}>{text}</span>
    },
    image: ({ node, key }) => {
      const image = node
      const href = asLink(image.linkTo) ?? ""
      const imgixFilter = disableImgBlueFilter ? {} : imgixBlueFilter

      if (href) {
        return (
          <PrismicNextLink
            field={node.linkTo}
            linkResolver={linkResolver}
            prefetch={false}
            rel="nofollow noopener noreferrer"
            className="my-5 w-full"
            key={key}>
            <PNImage
              src={image}
              loading="lazy"
              width={867}
              imgixParams={{ ...imgixFilter, w: 867, fit: "max" }}
              className="my-[52px] w-full overflow-hidden bg-neutral-100 object-contain"
            />
          </PrismicNextLink>
        )
      }

      return (
        <PNImage
          src={image}
          loading="lazy"
          width={867}
          imgixParams={{ ...imgixFilter, w: 867, fit: "max" }}
          className="my-[52px] w-full overflow-hidden bg-neutral-100 object-contain"
          key={key}
        />
      )
    },
    embed: ({ node, key }) => (
      <div
        data-oembed={node.oembed.author_url}
        data-oembed-type={node.oembed.type}
        data-oembed-provider={node.oembed.provider_name}
        className={node.oembed.provider_name ?? undefined}
        dangerouslySetInnerHTML={{ __html: node.oembed.html ?? "" }}
        key={key}
      />
    ),
    hyperlink: ({ node, children, key }) => {
      const link = interpolateLink(node, interpolations)
      const href = asLink(link.data) ?? ""

      // No follow external links and open in new tab
      const externalLink = isExternalUrl(href)
      // Replace absolute URLs with relative URLs
      const legacyDomain = "https://usebounce.com"
      if (!externalLink && href.includes(legacyDomain)) {
        link.data.url = convertLegacyUrl({ url: href })
      }
      const rel = externalLink ? "noopener noreferrer nofollow" : undefined
      const target = externalLink ? "_blank" : undefined

      const onClick = !externalLink
        ? () => Analytics.track(InteractionEvents.InternalLinkPressed, { name: "RichText/Hyperlink" })
        : noop

      return (
        <PrismicNextLink
          field={link.data}
          linkResolver={linkResolver}
          prefetch={false}
          rel={rel}
          key={key}
          target={target}
          onClick={onClick}
          className="font-semibold text-primary">
          {interpolate(children)}
        </PrismicNextLink>
      )
    },
    span: ({ text: children, key }) => {
      const text = interpolate(children)
      const result: React.ReactNode[] = []

      let i = 0
      for (const line of text.split("\n")) {
        if (i > 0) {
          result.push(<br key={`${i}__break`} />)
        }

        result.push(<React.Fragment key={`${i}__line`}>{line}</React.Fragment>)

        i++
      }

      return <React.Fragment key={key}>{result}</React.Fragment>
    },
  }
}
